import React from 'react';

import { TimeBlock } from '@go-shared/types/time';
import { TypeaheadItem, InputElement, makeItem, Typeahead } from '@spark-labs/avocado-ui';
import { getBlocksInRange } from '@go-shared/lib/grid';
import { DAY_LENGTH, DEFAULT_HOUR_FORMAT } from '@go-shared/lib/time';
import Config from '@go-client/lib/config';

export interface TimeInputProps {
  label?: string;
  value?: string;
  startTime?: number; // In minutes
  blockLength?: number; // In minutes
  timeFormat?: '12' | '24';
  onSelect?: (block: TimeBlock) => void;
  onChange?: (event: React.ChangeEvent<InputElement>) => void;
}

export interface TimeInputState {
  typeaheadValue: string;
  selectedBlock?: TimeBlock;
}

const DEFAULT_START_TIME = 0;

export class TimeInput extends React.Component<TimeInputProps, TimeInputState> {
  public readonly state: TimeInputState = {
    typeaheadValue: '',
  };

  private timeItems: TypeaheadItem[];
  private timeBlocks: TimeBlock[];

  constructor(props: TimeInputProps) {
    super(props);

    let startTime = props.startTime === undefined ? DEFAULT_START_TIME : props.startTime;
    let blockLength = props.blockLength || Config.DEFAULT_BLOCK_LENGTH;

    this.timeBlocks = getBlocksInRange(
      0,
      DAY_LENGTH,
      blockLength,
      startTime,
      props.timeFormat || DEFAULT_HOUR_FORMAT
    );

    this.timeItems = this.timeBlocks.map(b => makeItem(String(b.index), b.name));

    this.handleSelect = this.handleSelect.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.syncTypeaheadValue = this.syncTypeaheadValue.bind(this);
  }

  public componentDidMount() {
    this.syncTypeaheadValue();
  }

  public componentDidUpdate(oldProps: TimeInputProps) {
    if (oldProps.value !== this.props.value) {
      this.syncTypeaheadValue();
    }
  }

  public render() {
    return (
      <Typeahead
        inputClassName={this.props.value !== this.state.typeaheadValue ? 'text-red' : undefined}
        label={this.props.label}
        value={this.state.typeaheadValue}
        onSelect={this.handleSelect}
        items={this.timeItems}
        onChange={this.handleChange}
      />
    );
  }

  private handleSelect(item: TypeaheadItem) {
    let selectedBlock = this.timeBlocks.find(b => String(b.index) === item.key);

    if (!selectedBlock) {
      throw new Error(`Could not find block ${item}`);
    }

    this.setState({ selectedBlock });

    if (this.props.onSelect) this.props.onSelect(selectedBlock);
  }

  private handleChange(event: React.ChangeEvent<InputElement>) {
    this.setState({ typeaheadValue: event.target.value });
  }

  private syncTypeaheadValue() {
    this.setState({ typeaheadValue: this.props.value || '' });
  }
}
